
import React from 'react';
import { Briefcase, Users, Brain, File, Database, UserCheck, Code, GraduationCap, Globe, Skull } from 'lucide-react';
import { Skill } from './types';

export const skillsData: Skill[] = [
  {
    category: "AI Literacy & Prompting",
    icon: <Code className="h-6 w-6 text-sage-600" />,
    skills: [
      { 
        name: "AI Prompt Crafting", 
        description: "Creating clear instructions that yield useful AI outputs" 
      },
      { 
        name: "Task Automation", 
        description: "Using AI tools to streamline repetitive processes" 
      },
      { 
        name: "Workflow Enhancement", 
        description: "Integrating AI capabilities into existing systems" 
      },
      { 
        name: "Learning Mindset", 
        description: "Continuously exploring and adapting to new AI capabilities" 
      }
    ]
  },
  {
    category: "Client Relations & Communication",
    icon: <Users className="h-6 w-6 text-sage-600" />,
    skills: [
      { 
        name: "Active Listening", 
        description: "Fully engaging with clients to understand their needs and concerns" 
      },
      { 
        name: "Conflict Resolution", 
        description: "De-escalating tense situations through empathy and clear communication" 
      },
      { 
        name: "Clear Communication", 
        description: "Delivering complex information in an accessible, understandable way" 
      },
      { 
        name: "Empathetic Response", 
        description: "Meeting people where they are with genuine care and understanding" 
      }
    ]
  },
  {
    category: "Office Coordination & Workflow",
    icon: <Briefcase className="h-6 w-6 text-sage-600" />,
    skills: [
      { 
        name: "Process Optimization", 
        description: "Streamlining systems to improve efficiency and reduce bottlenecks" 
      },
      { 
        name: "Schedule Management", 
        description: "Coordinating appointments and timelines across multiple stakeholders" 
      },
      { 
        name: "Resource Allocation", 
        description: "Ensuring materials and staff are deployed effectively" 
      },
      { 
        name: "Documentation", 
        description: "Creating and maintaining clear records and process documentation" 
      },
      { 
        name: "AI Literacy & Prompting (Beginner)", 
        description: "Practicing the use of large language models and generative tools for writing, research, and task automation" 
      }
    ]
  },
  {
    category: "Behavioral Data Analysis",
    icon: <Brain className="h-6 w-6 text-sage-600" />,
    skills: [
      { 
        name: "Research Methods", 
        description: "Designing and implementing structured research protocols",
        expertise: "Proficient in analyzing behavioral patterns and data, utilizing statistical methods to interpret psychological research findings." 
      },
      { 
        name: "Pattern Recognition", 
        description: "Identifying trends and insights from behavioral observations" 
      },
      { 
        name: "Statistical Basics", 
        description: "Understanding fundamental statistical concepts for data interpretation" 
      },
      { 
        name: "Behavioral Assessment", 
        description: "Evaluating and documenting behavioral patterns in research settings" 
      }
    ]
  },
  {
    category: "Microsoft Office & Google Workspace",
    icon: <File className="h-6 w-6 text-sage-600" />,
    skills: [
      { 
        name: "Word/Docs", 
        description: "Creating professional documents, reports, and correspondence",
        expertise: "Experienced in creating and managing documents, spreadsheets, and presentations using Microsoft Office Suite and Google Workspace tools."
      },
      { 
        name: "Excel/Sheets", 
        description: "Building data tracking systems and analytical spreadsheets" 
      },
      { 
        name: "PowerPoint/Slides", 
        description: "Designing clear, engaging presentations for diverse audiences" 
      },
      { 
        name: "Outlook/Gmail", 
        description: "Managing communication efficiently with professional email practices" 
      }
    ]
  },
  {
    category: "CRM & Insurance Software",
    icon: <Database className="h-6 w-6 text-sage-600" />,
    skills: [
      { 
        name: "Client Management Systems", 
        description: "Tracking and maintaining client information and interactions",
        expertise: "Skilled in using Customer Relationship Management systems and insurance software for client data management and policy processing."
      },
      { 
        name: "State Farm Tools", 
        description: "Processing insurance quotes, claims, and policy management" 
      },
      { 
        name: "Records Management", 
        description: "Maintaining detailed, accessible client and policy records" 
      },
      { 
        name: "Data Entry", 
        description: "Accurate and efficient input of client and policy information" 
      }
    ]
  },
  {
    category: "Team Collaboration",
    icon: <UserCheck className="h-6 w-6 text-sage-600" />,
    skills: [
      { 
        name: "Project Coordination", 
        description: "Aligning team members around shared goals and timelines" 
      },
      { 
        name: "Meeting Facilitation", 
        description: "Leading productive discussions that respect everyone's time" 
      },
      { 
        name: "Constructive Feedback", 
        description: "Offering insights to help team members grow and improve" 
      },
      { 
        name: "Team Support", 
        description: "Being reliable and present for colleagues during challenging projects" 
      }
    ]
  },
  {
    category: "Youth Psychology Applications",
    icon: <GraduationCap className="h-6 w-6 text-sage-600" />,
    skills: [
      { 
        name: "Development Awareness", 
        description: "Understanding age-appropriate approaches for children and teens" 
      },
      { 
        name: "Engagement Techniques", 
        description: "Creating activities that foster participation and growth" 
      },
      { 
        name: "Behavioral Support", 
        description: "Implementing positive reinforcement and clear boundaries" 
      },
      { 
        name: "Positive Reinforcement", 
        description: "Encouraging growth through recognition of achievements" 
      }
    ]
  },
  {
    category: "Adaptability & Multicultural Awareness",
    icon: <Globe className="h-6 w-6 text-sage-600" />,
    skills: [
      { 
        name: "Cross-Cultural Communication", 
        description: "Adjusting communication style to bridge cultural differences" 
      },
      { 
        name: "Situational Flexibility", 
        description: "Thriving in changing environments and unexpected challenges" 
      },
      { 
        name: "Diverse Perspectives", 
        description: "Valuing and incorporating multiple viewpoints in problem-solving" 
      },
      { 
        name: "Cultural Sensitivity", 
        description: "Respecting and honoring different cultural norms and practices" 
      }
    ]
  },
  {
    category: "Forensic Anthropology",
    icon: <Skull className="h-6 w-6 text-sage-600" />,
    skills: [
      { 
        name: "Skeletal Analysis", 
        description: "Examining human remains to identify biological characteristics",
        expertise: "Knowledgeable in forensic anthropological techniques, including skeletal analysis and the application of anthropology in legal investigations."
      },
      { 
        name: "Forensic Identification", 
        description: "Techniques for identifying individuals from remains" 
      },
      { 
        name: "Legal Applications", 
        description: "Application of anthropological methods in legal contexts" 
      },
      { 
        name: "Archaeological Methods", 
        description: "Techniques for recovery and documentation of remains" 
      }
    ]
  }
];
